/* iter55g44 — Bookstore password reset landing page.
 *
 * The email sent by /api/bookstore/auth/password-reset/request links
 * here with `?token=...`. This page validates the token client-side by
 * rendering the form, then POSTs to /confirm. On success it redirects
 * to /bookstore/login so the user can sign in with their new password.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BookOpen, Lock, Loader2, ChevronLeft } from 'lucide-react';
import { useSeoMeta } from '@/lib/useSeoMeta';
import { bookstoreApi } from '@/lib/bookstoreApi';
import PasswordStrengthMeter, { scorePassword } from '@/components/PasswordStrengthMeter';
import { Link } from 'react-router-dom';

export default function BookstoreResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw]     = useState('');
  const [pw2, setPw2]   = useState('');
  const [busy, setBusy] = useState(false);

  useSeoMeta('/bookstore/reset-password', {
    title: 'Reset password · Crown & Rose',
    description: 'Set a new password for your Crown & Rose store account.',
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Missing reset token. Please use the link from your email.');
      return;
    }
    if (pw !== pw2) {
      toast.error('Passwords do not match.');
      return;
    }
    if (scorePassword(pw).score < 2) {
      toast.error('Please choose a stronger password.');
      return;
    }
    setBusy(true);
    try {
      await bookstoreApi.confirmPasswordReset(token, pw);
      toast.success('Password updated. Please sign in.');
      navigate('/bookstore/login', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-8 text-foreground" data-testid="bs-reset-page">
      <div className="w-full max-w-md">
        <Link to="/bookstore/login" data-testid="bs-reset-back" className="mb-4 inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground">
          <ChevronLeft className="h-3 w-3" /> Back to sign in
        </Link>
        <div className="rounded-xl border border-border bg-card p-7 shadow-panel-active">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <BookOpen className="h-4 w-4" />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-muted-foreground">Crown & Rose</div>
              <div className="text-[15px] font-semibold">Choose a new password</div>
            </div>
          </div>

          {!token && (
            <div className="mb-3 rounded-md border border-amber-400/60 bg-amber-50/60 p-3 text-[12px] text-amber-900" data-testid="bs-reset-missing-token">
              This page needs a reset token. Please use the link from your email, or{' '}
              <Link to="/bookstore/login?forgot=1" className="font-semibold underline">request a fresh link</Link>.
            </div>
          )}

          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-[11.5px] text-muted-foreground">New password</span>
              <div className="relative">
                <Lock className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="password" required minLength={8}
                  value={pw} onChange={(e) => setPw(e.target.value)}
                  placeholder="Minimum 8 characters"
                  data-testid="bs-reset-password"
                  className="w-full rounded-md border border-border bg-background py-2 pl-8 pr-3 text-[13px] outline-none focus:border-primary"
                />
              </div>
              <PasswordStrengthMeter password={pw} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11.5px] text-muted-foreground">Confirm new password</span>
              <div className="relative">
                <Lock className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="password" required minLength={8}
                  value={pw2} onChange={(e) => setPw2(e.target.value)}
                  placeholder="Type it again"
                  data-testid="bs-reset-password-confirm"
                  className="w-full rounded-md border border-border bg-background py-2 pl-8 pr-3 text-[13px] outline-none focus:border-primary"
                />
              </div>
            </label>
            <button
              type="submit" disabled={busy || !token}
              data-testid="bs-reset-submit"
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-[13px] font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Update password
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
